import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback } from 'react';
// utils
import axios from '../services/axios';
//
import { isValidToken, setSession } from './utils';

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'UPDATE_USER') {
    return {
      ...state,
      isAuthenticated: true,
      user: { ...state.user, ...action.payload.user },
    };
  }
  if (action.type === 'CONFIRM_USER') {
    return {
      ...state,
      isAuthenticated: false,
      user: { ...state.user, ...action.payload.user },
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : '';      

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get('/api/MarketClient/login');
        const { user } = response.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/MarketClient/login', {
        email,
        password,
        username: ''
      });
  
      const result = response.data?.result;
  
      if (!result || result.statusCode !== 200 || !result.jsonObject) {
        // Puedes lanzar un error personalizado para manejarlo en el `onSubmit`
        throw new Error(result?.reasonPhrase || 'Error al iniciar sesión');
      }
  
      const { accessToken, user } = result.jsonObject;
  
      setSession(accessToken);
  
      dispatch({
        type: 'LOGIN',
        payload: { user },
      });
  
      return true; // para saber que todo salió bien
    } catch (error) {
      console.error("Error en login:", error.message);
      throw error; // para que el `onSubmit` pueda mostrar un mensaje al usuario
    }
  };
  

  // REGISTER
  const register = async (email, username, password, first_name, last_name, buyer_type) => {
    const response = await axios.post('/api/MarketClient/register', {
      email,
      username,
      password,
      first_name,
      last_name,
      buyer_type,
      status: 1
    });
    const { accessToken, user } = response.data.jsonObject;

    // localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  // CONFIRM_USER
  const confirmUser = async (email, confirmation_num) => {
    const response = await axios.put('/api/MarketClient/userConfirm', {
      email,
      confirmation_num
    });
    console.log(response)
    return { data: response.data };
  };

  // UPDATE
  const updateUser = async (updatedUserData) => {
    const response = await axios.put('/api/MarketClient/update', updatedUserData);
    const responseData = response.data.jsonObject;
    
    dispatch({
      type: 'UPDATE_USER',
      payload: { user: responseData },
    });
  };

  const sendConfirmationNum = async (email) => {
    const response = await axios.post(`/api/MarketClient/sendConfirmationNum?email=${email}`);
    const responseData = response.data;
    return responseData;
  };

  const changePassword = async (id, oldPassword, newPassword) => {
    try {
      const response = await axios.put('/api/MarketClient/change-password', {
        id,
        oldPassword,
        newPassword,
      });
  
      if (response.status === 200) {
        // Si la respuesta es exitosa, puedes devolver un mensaje de éxito
        return { success: true, message: 'Contraseña cambiada con éxito' };
      }
      return { success: false, message: 'No se pudo cambiar la contraseña' };
    } catch (error) {
      console.error(error);
      // Si ocurre un error, retornamos un mensaje de error genérico
      return { success: false, message: 'Error al cambiar la contraseña' };
    }
  }; 
  
  const resetPassword = async ({ email, confirmation_num, newPassword }) => {
    try {
      const response = await axios.put('/api/MarketClient/reset-password', {
        email,
        confirmation_num,
        newPassword,
      });
      
      return response.data;
    } catch (error) {
      console.error(error);
      // Si ocurre un error, retornamos un mensaje de error genérico
      return { success: false, message: 'Error al cambiar la contraseña' };
    }
  }; 

  // LOGOUT
  const logout = async () => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        confirmUser,
        updateUser,
        sendConfirmationNum,
        resetPassword,
        changePassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
